import { Cause, Effect, Fiber, Layer, Ref, Schema, Scope, Semaphore } from "effect";
import { makeSessionOwner } from "../../state/session-owner.ts";
import { ResourceTabKind, resourceTabFocusFailure, type ResourceTabFocusFailure } from "./resource-focus-model.ts";
import { ResourceTabFocusIo, ResourceTabFocusSessions, type ResourceTabFocusSession } from "./resource-focus-service.ts";

export const ResourceTabFocusSessionsLive = (root: Scope.Scope) => Layer.effect(ResourceTabFocusSessions, Effect.gen(function* () {
  const io = yield* ResourceTabFocusIo, sessions = yield* makeSessionOwner(root);
  const failures = yield* Ref.make<readonly Cause.Cause<ResourceTabFocusFailure>[]>([]);
  const retain = (cause: Cause.Cause<ResourceTabFocusFailure>) => cause.reasons.some(reason => reason._tag !== "Interrupt")
    ? Ref.update(failures, values => [...values, cause]) : Effect.void;
  const acquire = (caller: Scope.Scope, isCurrent: (target: HTMLElement, kind: ResourceTabKind) => unknown) => Effect.gen(function* () {
    const owner = yield* Scope.Scope, gate = yield* Semaphore.make(1);
    const token = yield* Ref.make<object | null>(null), pending = yield* Ref.make<Fiber.Fiber<void> | null>(null);
    const alive = () => root.state._tag !== "Closed" && owner.state._tag !== "Closed" && caller.state._tag !== "Closed";
    const focus = (target: HTMLElement | null, input: unknown) => Effect.suspend(() => alive() ? gate.withPermit(Effect.gen(function* () {
      const kind = yield* Schema.decodeUnknownEffect(ResourceTabKind)(input).pipe(Effect.mapError(resourceTabFocusFailure));
      if (!target) return;
      const identity = {};
      yield* Ref.set(token, identity);
      const previous = yield* Ref.getAndSet(pending, null);
      if (previous) yield* Fiber.interrupt(previous);
      if (!alive()) return;
      const work = Effect.uninterruptibleMask(restore => restore(Effect.gen(function* () {
        yield* io.frame.pipe(Effect.flatMap(value => Schema.decodeUnknownEffect(Schema.Undefined)(value).pipe(Effect.mapError(resourceTabFocusFailure))));
        if (!alive() || Ref.getUnsafe(token) !== identity) return;
        const current = yield* Effect.try({ try: () => isCurrent(target, kind), catch: resourceTabFocusFailure }).pipe(
          Effect.flatMap(value => Schema.decodeUnknownEffect(Schema.Boolean)(value).pipe(Effect.mapError(resourceTabFocusFailure)))
        );
        if (!current || !alive() || Ref.getUnsafe(token) !== identity) return;
        yield* io.focus(target, kind).pipe(Effect.flatMap(value => Schema.decodeUnknownEffect(Schema.Undefined)(value).pipe(Effect.mapError(resourceTabFocusFailure))));
      })).pipe(Effect.catchCause(retain))).pipe(Effect.provideService(Scope.PreserveFinalizerFailures, true));
      const fiber = yield* work.pipe(Effect.forkIn(owner, { startImmediately: true }));
      yield* Ref.set(pending, fiber);
    })).pipe(Effect.catchCause(retain)) : Effect.interrupt);
    return { focus } satisfies ResourceTabFocusSession;
  });
  return ResourceTabFocusSessions.of({
    open: isCurrent => Effect.gen(function* () { const caller = yield* Scope.Scope; return yield* sessions.open(acquire(caller, isCurrent)); }),
    failures: Ref.get(failures)
  });
}));
